"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  abbreviateDigits,
  agreeingDecimals,
  binet,
  decimalQuotient,
  estimatedDigits,
  fibonacci,
  fibonacciRange,
  goldenRatio,
  type IndexRead,
  MAX_BINET_INDEX,
  MAX_COUNT,
  MAX_INDEX,
  RATIO_DECIMALS,
  readIndex,
} from "@/lib/tool-kit/number-theory/fibonacci";

/** English strings. A translated content file overrides these through `ui`. */
export const FIBONACCI_CALCULATOR_STRINGS = {
  index: "Which term, n",
  indexHint: "A whole number from 0 to {max}. F(0) = 0, F(1) = 1.",
  results: "Results",
  term: "F({n})",
  digits: "Digits",
  termValue: "Value of F({n})",
  copyValue: "Copy all digits",
  copied: "Copied!",
  summary: "F({n}) = {value}",
  shortened:
    "The number has {digits} digits; the cards show its first and last digits and the copy button copies every one.",
  binetHeading: "Binet's formula",
  binetIntro:
    "F(n) = (φⁿ − ψⁿ) ÷ √5, where φ = (1 + √5) ÷ 2 ≈ 1.6180339887 and ψ = (1 − √5) ÷ 2 ≈ −0.6180339887. Because |ψ| < 1 the second term fades away, so F(n) is φⁿ ÷ √5 rounded to the nearest whole number.",
  binetPhi: "φ^{n} ≈ {value}",
  binetPsi: "ψ^{n} ≈ {value}",
  binetDominant: "φ^{n} ÷ √5 ≈ {value}",
  binetRounded:
    "(φ^{n} − ψ^{n}) ÷ √5, rounded in double-precision arithmetic = {value}",
  binetExact: "This equals the exact F({n}) from the exact calculation.",
  binetDrift:
    "This differs from the exact F({n}) = {exact}. A double carries about 16 significant digits, so Binet's formula in ordinary floating point drifts from around n = 70. The exact figures here use whole-number arithmetic.",
  binetTooLarge:
    "Past n = {max} the floating-point formula overflows, so it is not evaluated. The digit count still follows from it: F(n) has ⌊n·log₁₀φ − log₁₀√5⌋ + 1 = {digits} digits.",
  ratioHeading: "Ratio of consecutive terms",
  ratioIntro:
    "F(n + 1) ÷ F(n) approaches the golden ratio φ as n grows, alternating above and below it.",
  ratioPhi: "φ = {value}…",
  ratioAt: "F({n1}) ÷ F({n}) = {value}…",
  ratioAgree: "These agree with φ for the first {count} decimal places.",
  ratioNone: "F(0) = 0, so F(1) ÷ F(0) is undefined. Try n = 1 or more.",
  sequenceHeading: "The sequence",
  from: "List from term",
  count: "How many terms",
  countHint: "Up to {max} terms.",
  colN: "n",
  colTerm: "F(n)",
  colRatio: "F(n + 1) ÷ F(n), cut to 10 places",
  absent: "—",
  enter: "Enter the term you want, for example 100.",
  invalid: "Enter a whole number, with no decimals, fractions or commas.",
  negative: "Enter 0 or more. This calculator covers F(0), F(1), F(2), …",
  tooLarge: "Enter a number no larger than {max}.",
  listInvalid: "Enter whole numbers for the start and the count.",
} as const;

type Strings = typeof FIBONACCI_CALCULATOR_STRINGS;

const FULL_DIGITS_LIMIT = 40;
const LIST_RATIO_DECIMALS = 10;
const MONO_LINES =
  "space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed";
const PROSE_SM = "text-muted-foreground text-sm";

function approx(
  value: number,
  fmt: (v: number, o?: Intl.NumberFormatOptions) => string
): string {
  const size = Math.abs(value);
  if (size >= 1e9 || (size > 0 && size < 1e-4)) {
    return fmt(value, { notation: "scientific", maximumSignificantDigits: 10 });
  }
  return fmt(value, { maximumSignificantDigits: 12 });
}

/** Every digit of the double, so its error against the exact term is visible. */
function wholeDouble(
  value: number,
  fmt: (v: number, o?: Intl.NumberFormatOptions) => string
): string {
  return Number.isFinite(value) && Math.abs(value) >= 1e9
    ? BigInt(value).toString()
    : approx(value, fmt);
}

function BinetSection({
  n,
  exact,
  strings,
}: {
  n: number;
  exact: bigint;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  if (n > MAX_BINET_INDEX) {
    return (
      <>
        <h3 className={WIDGET_H3}>{strings.binetHeading}</h3>
        <p className={PROSE_SM}>{strings.binetIntro}</p>
        <Notice>
          {t(strings.binetTooLarge, {
            max: fmt(MAX_BINET_INDEX),
            digits: fmt(estimatedDigits(n)),
          })}
        </Notice>
      </>
    );
  }
  const result = binet(n, exact);
  const at = String(n);
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.binetHeading}</h3>
      <p className={PROSE_SM}>{strings.binetIntro}</p>
      <div className={MONO_LINES}>
        <p>
          {t(strings.binetPhi, { n: at, value: approx(result.phiPower, fmt) })}
        </p>
        <p>
          {t(strings.binetPsi, { n: at, value: approx(result.psiPower, fmt) })}
        </p>
        <p>
          {t(strings.binetDominant, {
            n: at,
            value: approx(result.dominant, fmt),
          })}
        </p>
        <p>
          {t(strings.binetRounded, {
            n: at,
            value: wholeDouble(result.rounded, fmt),
          })}
        </p>
      </div>
      <Notice>
        {result.exact
          ? t(strings.binetExact, { n: at })
          : t(strings.binetDrift, {
              n: at,
              exact: abbreviateDigits(exact, FULL_DIGITS_LIMIT),
            })}
      </Notice>
    </>
  );
}

function RatioSection({ n, strings }: { n: number; strings: Strings }) {
  const fmt = useToolNumber();
  const ratio = useMemo(
    () =>
      n === 0
        ? null
        : decimalQuotient(fibonacci(n + 1), fibonacci(n), RATIO_DECIMALS),
    [n]
  );
  const phi = useMemo(() => goldenRatio(RATIO_DECIMALS), []);
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.ratioHeading}</h3>
      <p className={PROSE_SM}>{strings.ratioIntro}</p>
      {ratio === null ? (
        <Notice>{strings.ratioNone}</Notice>
      ) : (
        <div className={MONO_LINES}>
          <p>{t(strings.ratioPhi, { value: phi })}</p>
          <p>
            {t(strings.ratioAt, {
              n1: String(n + 1),
              n: String(n),
              value: ratio,
            })}
          </p>
          <p className="font-sans">
            {t(strings.ratioAgree, {
              count: fmt(agreeingDecimals(ratio, phi)),
            })}
          </p>
        </div>
      )}
    </>
  );
}

function TermResults({ n, strings }: { n: number; strings: Strings }) {
  const fmt = useToolNumber();
  const value = useMemo(() => fibonacci(n), [n]);
  const text = value.toString();
  const short = text.length > FULL_DIGITS_LIMIT;
  const summary = t(strings.summary, { n: String(n), value: text });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.term, { n: String(n) })}
          value={abbreviateDigits(value, FULL_DIGITS_LIMIT)}
        />
        <StatCard label={strings.digits} value={fmt(text.length)} />
      </StatGrid>
      {short ? (
        <p className={PROSE_SM}>
          {t(strings.shortened, { digits: fmt(text.length) })}
        </p>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyValue}
        small
        text={summary}
      />
      <BinetSection exact={value} n={n} strings={strings} />
      <RatioSection n={n} strings={strings} />
    </>
  );
}

function SequenceTable({
  rawFrom,
  rawCount,
  strings,
}: {
  rawFrom: string;
  rawCount: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const from = readIndex(rawFrom);
  const count = readIndex(rawCount, MAX_COUNT);
  const rows = useMemo(() => {
    if (from.kind !== "ok" || count.kind !== "ok" || count.value === 0) {
      return null;
    }
    const terms = fibonacciRange(from.value, count.value + 1);
    return terms
      .slice(0, count.value)
      .map((term, offset) => [
        fmt(from.value + offset),
        abbreviateDigits(term, FULL_DIGITS_LIMIT),
        term === BigInt(0)
          ? strings.absent
          : decimalQuotient(terms[offset + 1], term, LIST_RATIO_DECIMALS),
      ]);
  }, [from, count, fmt, strings.absent]);
  if (rows === null) {
    return <ErrorNotice>{strings.listInvalid}</ErrorNotice>;
  }
  return (
    <DataTable
      head={[strings.colN, strings.colTerm, strings.colRatio]}
      rows={rows}
    />
  );
}

function TermOutcome({ read, strings }: { read: IndexRead; strings: Strings }) {
  const fmt = useToolNumber();
  switch (read.kind) {
    case "ok":
      return <TermResults n={read.value} strings={strings} />;
    case "empty":
      return <Notice>{strings.enter}</Notice>;
    case "negative":
      return <ErrorNotice>{strings.negative}</ErrorNotice>;
    case "too-large":
      return (
        <ErrorNotice>
          {t(strings.tooLarge, { max: fmt(MAX_INDEX) })}
        </ErrorNotice>
      );
    default:
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
}

export default function FibonacciCalculatorWidget() {
  const strings = useToolUi(FIBONACCI_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("100");
  const [rawFrom, setRawFrom] = useState("0");
  const [rawCount, setRawCount] = useState("20");
  const deferred = useDeferredValue(raw);
  const deferredFrom = useDeferredValue(rawFrom);
  const deferredCount = useDeferredValue(rawCount);
  const read = useMemo(() => readIndex(deferred), [deferred]);
  return (
    <>
      <TextField
        hint={t(strings.indexHint, { max: fmt(MAX_INDEX) })}
        id="fib-index"
        label={strings.index}
        mono
        numeric
        onChange={setRaw}
        value={raw}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <TermOutcome read={read} strings={strings} />
      </section>
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.sequenceHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="fib-from"
            label={strings.from}
            mono
            numeric
            onChange={setRawFrom}
            value={rawFrom}
          />
          <TextField
            hint={t(strings.countHint, { max: fmt(MAX_COUNT) })}
            id="fib-count"
            label={strings.count}
            mono
            numeric
            onChange={setRawCount}
            value={rawCount}
          />
        </div>
        <div aria-live="polite">
          <SequenceTable
            rawCount={deferredCount}
            rawFrom={deferredFrom}
            strings={strings}
          />
        </div>
      </section>
    </>
  );
}
